import React, { useEffect, useLayoutEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { enterBookingScreen, focusMemberScreenTarget } from '../lib/useBookingScreenEntry'
import { useModalVisualViewport } from '../lib/useModalVisualViewport'
import { acquireModalScrollLock } from '../lib/modalScrollLock'

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
const MODAL_SIZES = new Set(['default', 'compact', 'dashboard', 'fullscreen'])

/** Shared, accessible shell for the dashboard's full-size content dialogs. */
export default function MemberDashboardModal({
  children,
  labelledBy,
  onClose,
  closeLabel,
  testId,
  bodyClassName = '',
  screenKey,
  size = 'default',
  closeOnBackdrop = true,
  closeOnEscape = true,
  showCloseButton = true,
}) {
  const shellRef = useRef(null)
  useModalVisualViewport(shellRef)
  const dialogRef = useRef(null)
  const hasEntered = useRef(false)
  const restoreFocusRef = useRef(null)
  useLayoutEffect(() => {
    if (!hasEntered.current) restoreFocusRef.current = document.activeElement
    enterBookingScreen(dialogRef.current, { includeAncestors: false })
    hasEntered.current = true
  }, [screenKey])
  const closeOnEscapeRef = useRef(closeOnEscape)
  closeOnEscapeRef.current = closeOnEscape
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return undefined

    const releaseScrollLock = acquireModalScrollLock(testId || 'member-dashboard-modal')

    const focusable = () => Array.from(dialog.querySelectorAll(FOCUSABLE_SELECTOR))
      .filter((element) => {
        if (element.hidden || element.closest('[hidden], [inert]')) return false
        if (element.getAttribute('aria-hidden') === 'true' || element.getAttribute('aria-disabled') === 'true') return false
        const styles = window.getComputedStyle(element)
        return styles.display !== 'none' && styles.visibility !== 'hidden'
      })

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (!closeOnEscapeRef.current) return
        event.preventDefault()
        console.info('[member-modal]', { event: 'modal_close_requested', owner: testId || 'member-dashboard-modal', method: 'escape' })
        onCloseRef.current()
        return
      }
      if (event.key !== 'Tab') return
      const elements = focusable()
      if (!elements.length) {
        event.preventDefault()
        dialog.focus()
        return
      }
      const first = elements[0]
      const last = elements[elements.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      releaseScrollLock()
      if (dialog.contains(document.activeElement)) {
        document.activeElement?.blur?.()
      }
      focusMemberScreenTarget(restoreFocusRef.current)
    }
  }, [testId])

  const requestClose = (method) => {
    console.info('[member-modal]', { event: 'modal_close_requested', owner: testId || 'member-dashboard-modal', method })
    onClose()
  }

  const resolvedSize = MODAL_SIZES.has(size) ? size : 'default'
  const isMobileMembersApp = Boolean(document.querySelector('.mp-app--mobile'))

  return createPortal(
    <div ref={shellRef} className={`mp-modal mp-dashboard-modal${isMobileMembersApp ? ' mp-dashboard-modal--mobile' : ''}`} data-testid={`${testId}-shell`}>
      <div
        className="mp-modal__backdrop"
        aria-hidden="true"
        onClick={closeOnBackdrop ? () => requestClose('backdrop') : undefined}
      />
      <div
        ref={dialogRef}
        className={`mp-modal__card mp-dashboard-modal__card mp-dashboard-modal__card--${resolvedSize}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        data-testid={testId}
        data-mp-static-focus="" tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
      >
        {showCloseButton ? (
          <button type="button" className="mp-modal__close" onClick={() => requestClose('close_button')} aria-label={closeLabel}>
            <X size={18} aria-hidden="true" />
          </button>
        ) : null}
        <div className={`mp-dashboard-modal__body ${bodyClassName}`.trim()}>{children}</div>
      </div>
    </div>,
    document.body,
  )
}
